import { hrefOf, type ClientCard as Card, type HealthDto } from "@nabla/api";
import { Loads } from "../client/index.ts";
import { Dot } from "../design/primitives/Dot.tsx";
import { formatTime, saoPaulo } from "../design/format/dates.ts";
import { healthText } from "../design/format/status.ts";
import { urgentLine } from "../features/accounts/parts/alerts.ts";
import { wordsOf } from "../words/session.ts";
import { titleText } from "../features/change/words.ts";
import { takeawaySentences } from "../features/overview/parts/takeaway.ts";
import { agencyCopy as t } from "./copy.ts";
import { intoClient } from "./navigation.ts";
import styles from "./agency.module.css";

const c = t.cartao;

function freshness(health: HealthDto, now: number): { readonly text: string; readonly late: boolean } | undefined {
  const warning = healthText(health, now);
  if (warning !== undefined) return { text: warning.text, late: true };
  return health.lastRunAt === undefined ? undefined : { text: c.leitura(formatTime(health.lastRunAt, saoPaulo)), late: false };
}

interface Props {
  readonly id: string;
  readonly name: string;
  readonly card: Card | null;
  readonly now: number;
}

export function ClientCard({ id, name, card, now }: Props) {
  const open = (
    <a className={styles.open} href={intoClient(id)}>
      {c.abrir}
    </a>
  );
  if (card === null) {
    return (
      <div role="group" aria-label={name} className={styles.client}>
        <h3 className={styles.clientName}>{name}</h3>
        <p className={styles.detail}>{c.semResposta}</p>
        {open}
      </div>
    );
  }
  const words = wordsOf(card, now);
  const [headline] = card.takeaway === null || card.window.noWholeDay ? [] : takeawaySentences(card.takeaway, card.window.days, words);
  const currency = card.derived.accounts[0]?.currency;
  const fresh = freshness(card.health, now);
  return (
    <div role="group" aria-label={name} className={styles.client}>
      <h3 className={styles.clientName}>
        <a href={intoClient(id)}>{name}</a>
      </h3>
      {headline === undefined ? null : <p className={styles.headline}>{headline}</p>}
      <p className={card.waitingCount > 0 ? styles.needs : styles.detail}>{c.esperam(card.waitingCount)}</p>
      {card.alerts.slice(0, 1).map((alert) => (
        <p key={alert.id} className={styles.alert}>
          <Dot kind="atencao" /> {urgentLine(alert, words, currency, now)}
          {card.alertsCount > 1 ? ` (${c.alertas(card.alertsCount)})` : ""}
        </p>
      ))}
      {card.decided.length === 0 ? null : (
        <>
          <p className={styles.small}>{c.decisoes}</p>
          <ul className={styles.decided}>
            {card.decided.map((change) => (
              <li key={change.id}>
                <Loads>{() => <a href={intoClient(id, hrefOf("mudanca", change.id))}>{titleText(change.title)}</a>}</Loads>
              </li>
            ))}
          </ul>
        </>
      )}
      {fresh === undefined ? null : <p className={fresh.late ? styles.late : styles.small}>{fresh.text}</p>}
      {open}
    </div>
  );
}
